import { usePathname } from "expo-router";
import { useEffect, useRef } from "react";
import { Platform } from "react-native";

import { centralMetricValue, centralPerformanceRoute } from "../lib/central-performance";
import { recordCentralClientMetric } from "../lib/firebase";
import { isCentralHost } from "../lib/hosting";

export function CentralPerformanceObserver() {
  const pathname = usePathname();
  const initialRoute = useRef(centralPerformanceRoute(pathname));

  useEffect(() => {
    if (Platform.OS !== "web" || !isCentralHost() || typeof performance === "undefined") return;
    const route = centralPerformanceRoute(pathname);
    const startedAt = performance.now();
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const value = centralMetricValue(performance.now() - startedAt);
        if (value !== null) void recordCentralClientMetric("route_render", value, route);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [pathname]);

  useEffect(() => {
    if (Platform.OS !== "web" || !isCentralHost() || typeof PerformanceObserver === "undefined") return;
    const route = initialRoute.current;
    const observers: PerformanceObserver[] = [];
    const observe = (type: string, callback: (entry: PerformanceEntry) => void) => {
      const supportedEntryTypes = PerformanceObserver.supportedEntryTypes;
      if (Array.isArray(supportedEntryTypes) && !supportedEntryTypes.includes(type)) return;
      try {
        const observer = new PerformanceObserver((list) => list.getEntries().forEach(callback));
        observer.observe({ buffered: true, type } as PerformanceObserverInit);
        observers.push(observer);
      } catch {
        // Browsers may not implement every observer type.
      }
    };
    observe("paint", (entry) => {
      if (entry.name === "first-contentful-paint") void recordCentralClientMetric("first_contentful_paint", entry.startTime, route);
    });
    observe("largest-contentful-paint", (entry) => void recordCentralClientMetric("largest_contentful_paint", entry.startTime, route));
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web" || !isCentralHost() || typeof PerformanceObserver === "undefined") return;
    const route = centralPerformanceRoute(pathname);
    const observers: PerformanceObserver[] = [];
    const observe = (type: string, callback: (entry: PerformanceEntry) => void) => {
      const supportedEntryTypes = PerformanceObserver.supportedEntryTypes;
      if (Array.isArray(supportedEntryTypes) && !supportedEntryTypes.includes(type)) return;
      try {
        const observer = new PerformanceObserver((list) => list.getEntries().forEach(callback));
        observer.observe({ type } as PerformanceObserverInit);
        observers.push(observer);
      } catch {
        // Browsers may not implement every observer type.
      }
    };
    let cumulativeLayoutShift = 0;
    let longTaskMs = 0;
    observe("layout-shift", (entry) => {
      const shift = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
      if (!shift.hadRecentInput) cumulativeLayoutShift += shift.value ?? 0;
    });
    observe("longtask", (entry) => { longTaskMs += entry.duration; });
    const flush = () => {
      if (cumulativeLayoutShift > 0) void recordCentralClientMetric("layout_shift_x1000", cumulativeLayoutShift * 1_000, route);
      if (longTaskMs > 0) void recordCentralClientMetric("long_task_total", longTaskMs, route);
      cumulativeLayoutShift = 0;
      longTaskMs = 0;
    };
    window.addEventListener("pagehide", flush, { once: true });
    return () => {
      flush();
      window.removeEventListener("pagehide", flush);
      observers.forEach((observer) => observer.disconnect());
    };
  }, [pathname]);

  return null;
}
